import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Plus, Edit, Trash2, Shield, UserCheck, User, Search,
  Loader2, MoreVertical, Mail, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../hooks/use-toast';
import { Checkbox } from '../../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeleteForm, setBulkDeleteForm] = useState({
    mode: 'selection',
    created_from: '',
    created_to: '',
    email_domain: '',
    country: '',
    include_verified: true,
    include_unverified: true,
    include_active: true,
    include_inactive: true,
    confirm_text: '',
  });
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [trashUsers, setTrashUsers] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [isTrashDialogOpen, setIsTrashDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
    fetchUserTrash();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  const fetchUserTrash = async () => {
    setTrashLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/trash`);
      setTrashUsers(response.data?.trash || []);
    } catch (error) {
      setTrashUsers([]);
    }
    setTrashLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast({
        title: 'User Created',
        description: `${formData.email} has been added successfully.`
      });
      setIsCreateDialogOpen(false);
      setFormData({ email: '', name: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create user',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      const updateData = {
        name: formData.name,
        role: formData.role
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData);
      toast({
        title: 'User Updated',
        description: 'User information has been updated.'
      });
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update user',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast({
        title: 'User Deleted',
        description: 'User has been removed from the system.'
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const toggleUserSelection = (userId, checked) => {
    setSelectedUserIds((prev) => {
      if (checked) return [...new Set([...prev, userId])];
      return prev.filter((id) => id !== userId);
    });
  };

  const handleRunBulkDelete = async () => {
    if (!isSuperAdmin) {
      toast({ title: 'Access denied', description: 'Super admin only', variant: 'destructive' });
      return;
    }

    if (bulkDeleteForm.confirm_text.trim() !== 'DELETE 19000') {
      toast({ title: 'Confirmation mismatch', description: 'Type DELETE 19000 exactly', variant: 'destructive' });
      return;
    }

    try {
      setBulkDeleteLoading(true);
      const payload = {
        ...bulkDeleteForm,
        mode: bulkDeleteForm.mode,
        user_ids: selectedUserIds,
      };
      const response = await axios.post(`${API}/admin/users/bulk-delete`, payload);
      toast({ title: 'Bulk delete complete', description: `${response.data?.deleted_count || 0} users moved to trash.` });
      setIsBulkDeleteDialogOpen(false);
      setSelectedUserIds([]);
      setBulkDeleteForm((prev) => ({ ...prev, confirm_text: '' }));
      fetchUsers();
      fetchUserTrash();
    } catch (error) {
      toast({ title: 'Bulk delete failed', description: error.response?.data?.detail || 'Request failed', variant: 'destructive' });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleRestoreUser = async (trashId) => {
    try {
      await axios.post(`${API}/admin/users/trash/${trashId}/restore`);
      toast({ title: 'User restored', description: 'User has been restored from trash.' });
      fetchUsers();
      fetchUserTrash();
    } catch (error) {
      toast({ title: 'Restore failed', description: error.response?.data?.detail || 'Unable to restore user', variant: 'destructive' });
    }
  };

  const handleEmptyTrash = async () => {
    const confirmText = window.prompt('Type EMPTY USERS to permanently remove all users in trash:');
    if (confirmText !== 'EMPTY USERS') return;
    try {
      await axios.delete(`${API}/admin/users/trash/empty`);
      toast({ title: 'Trash emptied', description: 'User trash was permanently cleared.' });
      fetchUserTrash();
    } catch (error) {
      toast({ title: 'Empty trash failed', description: error.response?.data?.detail || 'Unable to empty trash', variant: 'destructive' });
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      password: '',
      role: user.role
    });
    setIsEditDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-purple-600"><Shield className="w-3 h-3 mr-1" />Super Admin</Badge>;
      case 'admin':
        return <Badge className="bg-[#6e2ea8]"><UserCheck className="w-3 h-3 mr-1" />Admin</Badge>;
      default:
        return <Badge variant="secondary"><User className="w-3 h-3 mr-1" />User</Badge>;
    }
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#6e2ea8]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-[#6e2ea8]" />
            User Management
          </h1>
          <p className="text-gray-500">Manage admin and staff accounts</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#6e2ea8] hover:bg-[#a01830]" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>Create an admin or staff account with role permissions.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="user-role-select"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {isSuperAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#6e2ea8] hover:bg-[#a01830]" data-testid="submit-create-user">
                  Create User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsTrashDialogOpen(true)} data-testid="open-user-trash-button">
              User Trash ({trashUsers.length})
            </Button>
            <Button variant="destructive" onClick={() => setIsBulkDeleteDialogOpen(true)} data-testid="open-bulk-user-delete-button">
              Bulk Delete Users
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#6e2ea8]/10 rounded-xl">
                <Users className="w-6 h-6 text-[#6e2ea8]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Super Admins</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'super_admin').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <UserCheck className="w-6 h-6 text-[#6e2ea8]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Admins</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>All Users</CardTitle>
              <CardDescription>A list of all registered users in the system</CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Select</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    {user.email !== currentUser?.email && (
                      <Checkbox checked={selectedUserIds.includes(user.id)} onCheckedChange={(checked) => toggleUserSelection(user.id, Boolean(checked))} data-testid={`select-user-${user.id}`} />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-[#6e2ea8] text-white text-sm">
                          {user.name?.charAt(0) || user.email?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.name || 'No name'}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(user)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {user.email !== currentUser?.email && (
                          <DropdownMenuItem 
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user profile, role, or reset password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <select
                id="edit-role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {isSuperAdmin && <option value="super_admin">Super Admin</option>}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#6e2ea8] hover:bg-[#a01830]">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-user-delete-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Delete Users (Soft Delete)</DialogTitle>
            <DialogDescription>
              Soft-delete users to trash. They can be restored later until trash is emptied.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Mode</Label>
                <select value={bulkDeleteForm.mode} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, mode: e.target.value }))} className="w-full border rounded px-3 py-2" data-testid="bulk-delete-mode-select">
                  <option value="selection">Selected Users</option>
                  <option value="filter_all">Filter + Delete All Matches</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Email domain filter</Label>
                <Input value={bulkDeleteForm.email_domain} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, email_domain: e.target.value }))} placeholder="example.com" data-testid="bulk-delete-domain-input" />
              </div>
              <div className="space-y-1">
                <Label>Country filter</Label>
                <Input value={bulkDeleteForm.country} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, country: e.target.value.toUpperCase() }))} placeholder="US" data-testid="bulk-delete-country-input" />
              </div>
              <div className="space-y-1">
                <Label>Created from</Label>
                <Input type="date" value={bulkDeleteForm.created_from} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, created_from: e.target.value }))} data-testid="bulk-delete-created-from-input" />
              </div>
              <div className="space-y-1">
                <Label>Created to</Label>
                <Input type="date" value={bulkDeleteForm.created_to} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, created_to: e.target.value }))} data-testid="bulk-delete-created-to-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="flex items-center gap-2"><Checkbox checked={bulkDeleteForm.include_verified} onCheckedChange={(checked) => setBulkDeleteForm((prev) => ({ ...prev, include_verified: Boolean(checked) }))} /> Include verified</label>
              <label className="flex items-center gap-2"><Checkbox checked={bulkDeleteForm.include_unverified} onCheckedChange={(checked) => setBulkDeleteForm((prev) => ({ ...prev, include_unverified: Boolean(checked) }))} /> Include unverified</label>
              <label className="flex items-center gap-2"><Checkbox checked={bulkDeleteForm.include_active} onCheckedChange={(checked) => setBulkDeleteForm((prev) => ({ ...prev, include_active: Boolean(checked) }))} /> Include active</label>
              <label className="flex items-center gap-2"><Checkbox checked={bulkDeleteForm.include_inactive} onCheckedChange={(checked) => setBulkDeleteForm((prev) => ({ ...prev, include_inactive: Boolean(checked) }))} /> Include inactive</label>
            </div>

            <div className="space-y-1">
              <Label>Type DELETE 19000 to confirm</Label>
              <Input value={bulkDeleteForm.confirm_text} onChange={(e) => setBulkDeleteForm((prev) => ({ ...prev, confirm_text: e.target.value }))} placeholder="DELETE 19000" data-testid="bulk-delete-confirm-input" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRunBulkDelete} disabled={bulkDeleteLoading} data-testid="run-bulk-delete-button">
                {bulkDeleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Execute Bulk Delete
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isTrashDialogOpen} onOpenChange={setIsTrashDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="user-trash-dialog">
          <DialogHeader>
            <DialogTitle>User Trash</DialogTitle>
            <DialogDescription>
              Restore accidentally deleted users, or permanently remove trash entries.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">Restore users or permanently clear trash.</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchUserTrash}>Refresh</Button>
              <Button variant="destructive" onClick={handleEmptyTrash}>Empty Trash</Button>
            </div>
          </div>
          {trashLoading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="max-h-96 overflow-auto border rounded">
              <table className="min-w-full text-sm" data-testid="user-trash-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Deleted At</th>
                    <th className="text-left p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trashUsers.map((row) => (
                    <tr key={row.id} className="border-t" data-testid={`user-trash-row-${row.id}`}>
                      <td className="p-2">{row.user_snapshot?.email}</td>
                      <td className="p-2">{row.user_snapshot?.name || '—'}</td>
                      <td className="p-2">{row.deleted_at ? new Date(row.deleted_at).toLocaleString() : '—'}</td>
                      <td className="p-2">
                        <Button variant="outline" size="sm" onClick={() => handleRestoreUser(row.id)} data-testid={`restore-user-${row.id}`}>Restore</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
